// New tagged slice for the migrated `_features/invoices` feature.
//
// The legacy `_features/invoices/api/invoiceApiSlice.js` (still used by
// InvoiceDataManagementTable, InvoiceWriteoffModal, and the customer-facing
// Pay portal - none in scope for this migration, see
// docs/known-issues.md) injects endpoints into the same shared apiSlice
// with no `overrideExisting` and no cache tags. Rather than fight over the
// same endpoint names (the same collision proposalApi.js/subscriptionApi.js
// each resolved by renaming), this file uses its own names and adds proper
// providesTags/invalidatesTags.
//
// 3 endpoints from the legacy slice are NOT carried forward -
// getInvoicePayment, logInvoicePayment, createSubscriptionFromInvoice -
// confirmed dead (zero consumers anywhere in the codebase, verified via
// grep before dropping them).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const invoiceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listInvoices: builder.query({
      query: (params) => ({
        url: apiRoutes.invoice.get.path,
        method: apiRoutes.invoice.get.reqType,
        params,
      }),
      providesTags: ["Invoices"],
    }),
    // Ids of companies with an open invoice past its due date (Companies "Past due" tab).
    getPastDueCompanyIds: builder.query({
      query: () => ({
        url: apiRoutes.invoice.pastDueCompanies.path,
        method: apiRoutes.invoice.pastDueCompanies.reqType,
      }),
      providesTags: ["Invoices"],
    }),
    submitInvoice: builder.mutation({
      query: (data) => ({
        url: apiRoutes.invoice.create.path,
        method: apiRoutes.invoice.create.reqType,
        body: data,
      }),
      invalidatesTags: ["Invoices"],
    }),
    resendInvoice: builder.mutation({
      query: (data) => ({
        url: apiRoutes.invoice.resend.path,
        method: apiRoutes.invoice.resend.reqType,
        body: data,
      }),
      invalidatesTags: ["Invoices"],
    }),
    // No invalidatesTags (unlike the other mutations here): InvoicePage's
    // handleWriteoffSubmit patches the written-off row directly into the
    // listInvoices cache via updateQueryData instead - invalidating the
    // blanket "Invoices" tag would force RTK Query to silently refetch (and
    // re-render) the entire list right behind that patch, reintroducing the
    // exact bug the patch fixes (SCRUM-158).
    writeoffInvoice: builder.mutation({
      query: (data) => ({
        url: apiRoutes.invoice.writeoff.path,
        method: apiRoutes.invoice.writeoff.reqType,
        body: data,
      }),
    }),
    // Live "what will actually get sent" render (see previewInvoiceDraft in
    // invoice.controller.js) against the form's current, unsaved draft
    // state. Never invalidates/is invalidated by anything - it has no side
    // effects to cache against.
    previewInvoice: builder.mutation({
      query: (data) => ({
        url: apiRoutes.invoice.preview.path,
        method: apiRoutes.invoice.preview.reqType,
        body: data,
      }),
    }),
  }),
});

export const {
  useListInvoicesQuery,
  useGetPastDueCompanyIdsQuery,
  useSubmitInvoiceMutation,
  useResendInvoiceMutation,
  useWriteoffInvoiceMutation,
  usePreviewInvoiceMutation,
} = invoiceApi;
